import { Service } from '@toeverything/infra';

import type { ServerService } from './server';

/**
 * ConfirmationEvent - Typed event for AI confirmation requests
 */
export interface ConfirmationEventDetail {
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  confirmationCopy?: {
    key: string;
    params?: Record<string, string | number | boolean | null>;
  };
  metadata?: Record<string, unknown>;
}

/**
 * Singleton event target for confirmation events.
 * Uses browser's native EventTarget API for proper cleanup.
 */
class ConfirmationEventBus extends EventTarget {
  private static instance: ConfirmationEventBus;

  static getInstance(): ConfirmationEventBus {
    if (!ConfirmationEventBus.instance) {
      ConfirmationEventBus.instance = new ConfirmationEventBus();
    }
    return ConfirmationEventBus.instance;
  }

  emit(detail: ConfirmationEventDetail): void {
    this.dispatchEvent(new CustomEvent('confirmation-required', { detail }));
  }

  subscribe(handler: (detail: ConfirmationEventDetail) => void): () => void {
    const listener = (event: Event) => {
      handler((event as CustomEvent<ConfirmationEventDetail>).detail);
    };
    this.addEventListener('confirmation-required', listener);
    return () => this.removeEventListener('confirmation-required', listener);
  }
}

// Export singleton instance
export const confirmationEventBus = ConfirmationEventBus.getInstance();

export class EventSourceService extends Service {
  constructor(private readonly serverService: ServerService) {
    super();
  }

  eventSource = (url: string, eventSourceInitDict?: EventSourceInit) => {
    const fullUrl = new URL(url, this.serverService.server.baseUrl);
    const eventSource = new EventSource(fullUrl, eventSourceInitDict);

    // Only intercept copilot chat streams for confirmation events
    if (fullUrl.pathname.includes('/api/copilot/chat/')) {
      eventSource.addEventListener(
        'confirmation-required',
        (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);

            // Emit via event bus - no blocking, immediate dispatch
            confirmationEventBus.emit({
              id: data.id,
              toolName: data.toolName,
              displayName: data.displayName || data.toolName,
              description: data.description,
              confirmationCopy: data.confirmationCopy,
              metadata: data.metadata,
            });
          } catch (error) {
            console.error(
              '[EventSourceService] Failed to parse confirmation-required event:',
              error
            );
          }
        }
      );
    }

    return eventSource;
  };
}
